// Construction de l'état d'un message assistant à partir des événements SSE v2
// (contrat C0, §1.1). Fonctions pures : (message, événement) → nouveau message.
//
// Forme d'un message assistant :
// {
//   id, serverMessageId, role: 'assistant',
//   content,            // réponse finale = tours non déplacés vers le raisonnement
//   turns: {0: '…'},    // texte brut de chaque tour
//   movedTurns: [0],    // tours passés au raisonnement (`turn_to_reasoning`)
//   reasoning: { items, startedAt, endedAt, status, answerStart },
//   isStreaming, model, modelId, modelLabel, usage, stopReason, dureeMs,
//   outcome: null | 'done' | 'error' | 'stopped',   // issue de la réponse, fixée à la fin
//   error: {code, message} | null,
//   feedback: {rating: 1|-1|null, reason, comment, status},
//   artifacts: [],      // visuels du message, dans l'ordre de production (voir plus bas)
//   effort: null | {niveau, source, effort, reflexion, raison, degrade},   // sprint 6 (start, done)
//   compaction: null | {mode, messages_couverts, tokens_estimes_avant, tokens_estimes_apres, reutilise},
//   continuations: 0,   // relances après une limite de longueur (événement `continuation`)
//   fichiers: [],       // liens vérifiés de fichiers (événement `file_links`, contrat v1.13)
// }
//
// Contrat v1.13 : `file_links` {fichiers: [{url, lien, type, libelle, vehicule?}], expire_le} :
// liens de fichiers (photos de véhicules, cartes grises) vus par le serveur dans les résultats
// d'outils de la réponse, servis par le même domaine (components/fichiers/fichiers.js). Ils
// rendent en vignette un lien ou une image Markdown du modèle qui recopie leur adresse.
//
// Sprint 6 (contrat v1.4, ajouts compatibles v1.3) :
//   - `start` / `done` : effort {niveau, source: "auto"|"utilisateur", effort, reflexion, raison?, degrade?} ;
//   - `context_compacted` {mode: "summary"|"server"|"truncation", messages_couverts,
//     tokens_estimes_avant, tokens_estimes_apres, reutilise} : marqueur au-dessus de la réponse ;
//   - `continuation` {turn, n} : le texte continue dans le même tour, seul le nombre est gardé.
//
// artifacts[] (contrat C0, §1.1 : `artifact_pending`, `artifact`, `artifact_error`) :
//   {id, status: 'pending'|'ready'|'error', kind, visual_type, title, spec, message, format?}
//   - pending : skeleton du type annoncé, à sa future place sous la réponse ;
//   - ready   : spécification validée par le backend (§2), rendue par VisualCard ;
//   - error   : spécification refusée, ou réponse terminée avant le visuel (message neutre).
//   - format  : documents (sprint 5, kind « document ») : pdf | docx | xlsx | pptx, annoncé
//     dès `artifact_pending` pour le badge du skeleton.
//
// Message « rapport composé » (DOC-17, sprint 5) : origin = 'report'. Produit dans
// l'interface (POST /api/reports/compose, sans appel au modèle), il porte le document
// dans artifacts[] ; il n'est jamais renvoyé au modèle (toHistory).
//
// reasoning.items, dans l'ordre chronologique :
//   {key, type: 'thinking', text}
//   {key, type: 'narration', turn, text, visible}   // visible une fois le tour déplacé
//   {key, type: 'step', step_id, tool, label, params, status: 'running'|'ok'|'error'|'cancelled',
//    startedAt, duree_ms, resume, error, execStartedAt?, progress?, endedAt?}
//   execStartedAt, progress, endedAt : propres à l'interface, jamais enregistrés (conversationCodec
//   ne recopie qu'une liste fermée de champs) ; voir `step_progress` ci-dessous. endedAt : réception
//   du premier step_done, pour ne pas prendre deux appels parallèles d'un même tour pour un
//   nouvel essai (utils/stepResume.js, analyseEssais).
//
// Progression d'une étape (contrat v1.8, MCP-13) : `step_progress` {step_id, ecoule_ms, etape?,
// n?, N?}. Sans n/N : début réel de l'exécution (place obtenue dans le limiteur du serveur), base
// du compteur de secondes (execStartedAt = réception − ecoule_ms, fixé une fois). Avec n/N
// (outils annotés progressHint) : progress = {etape, n, N}, n terminés sur N connus (N peut
// grandir), jamais en recul ; effacé à step_done et à la fin de la réponse.
//
// reasoning.status : 'idle' → 'running' → 'done' dès que la réponse finale s'écrit
// (rouvert si une étape ou une réflexion suit), puis 'done' | 'error' | 'stopped' à la fin.
// reasoning.answerStart : {turn, at}, premier texte d'un tour après la dernière activité
// du raisonnement ; `at` devient endedAt quand ce texte est bien la réponse finale.
//
// Lot 1 (vitesse, 29/10) : signe de vie immédiat et phases vraies.
//   - phase : {code, n?, N?} | null, en-tête du panneau pendant le flux. « analyse » dès la
//     création (la demande est partie), puis `phase` du serveur au vrai changement : reflexion
//     (1er jeton de réflexion d'un appel, même masquée), lecture (appel qui suit des résultats
//     d'outils), redaction (1er texte d'un appel), pages {n, N} (pages complétées), tri (lecture
//     ciblée). Effacée par une étape (la chronologie montre alors l'action) et à la fin. Ni
//     étape ni enregistrée (conversationCodec ne recopie qu'une liste fermée de champs).
//   - `step_pending` {step_id, tool, label} : étape annoncée dès le début du bloc tool_use,
//     avant ses arguments ; `step_start` du même step_id la précise (libellé, paramètres), sans
//     doublon. {step_id, retire: true} : bloc jamais exécuté, l'étape annoncée est retirée.

import { normalizeEffortInfo } from '../utils/effortPreference.js';
import { fusionnerFichiers, normalizeFichiers } from '../components/fichiers/fichiers.js';

// Texte au-delà duquel un tour est la réponse finale et non une phrase d'annonce
// avant un appel d'outil (« Je récupère les données carburant… »)
const ANSWER_MIN_CHARS = 160;

const COMPACTION_MODES = ['summary', 'server', 'truncation'];
const count = (v) => (v !== null && v !== '' && Number.isFinite(Number(v)) && Number(v) >= 0 ? Math.round(Number(v)) : null);

/** Lecture tolérante de `context_compacted` ; null si le mode est inconnu. */
export function normalizeCompaction(data) {
  if (!data || typeof data !== 'object' || !COMPACTION_MODES.includes(data.mode)) return null;
  return {
    mode: data.mode,
    messages_couverts: count(data.messages_couverts),
    tokens_estimes_avant: count(data.tokens_estimes_avant),
    tokens_estimes_apres: count(data.tokens_estimes_apres),
    reutilise: data.reutilise === true,
  };
}

// Bornes d'une progression (contrat v1.8) : 1 ≤ n ≤ N ≤ 100 000, libellé de 80 caractères au plus
const PROGRESS_N_MAX = 100000;
const PROGRESS_LABEL_MAX = 80;

/**
 * Lecture tolérante de `step_progress` : {step_id, ecoule_ms (entier ≥ 0 ou null), progress
 * ({etape, n, N} ou null)} ; null sans step_id.
 */
export function normalizeStepProgress(data) {
  if (!data || typeof data !== 'object' || data.step_id == null || data.step_id === '') return null;
  const ecoule = data.ecoule_ms;
  const ecoule_ms = typeof ecoule === 'number' && Number.isFinite(ecoule) && ecoule >= 0 ? Math.round(ecoule) : null;
  const { n, N } = data;
  let progress = null;
  if (Number.isInteger(n) && Number.isInteger(N) && n >= 1 && n <= N && N <= PROGRESS_N_MAX) {
    const etape = typeof data.etape === 'string'
      ? [...data.etape.replace(/\s+/g, ' ').trim()].slice(0, PROGRESS_LABEL_MAX).join('')
      : '';
    progress = { etape, n, N };
  }
  return { step_id: data.step_id, ecoule_ms, progress };
}

const sameProgress = (a, b) => !!a && !!b && a.n === b.n && a.N === b.N && a.etape === b.etape;

// Flux terminé sans `done` ni `error` (proxy, redémarrage du serveur, réseau)
export const CONNECTION_LOST = {
  code: 'NETWORK',
  message: 'La connexion a été interrompue avant la fin de la réponse.',
};

let keySeed = 0;
const nextKey = (prefix) => `${prefix}-${++keySeed}`;

// Phases connues (événement `phase`) ; un code inconnu est ignoré
const PHASES = ['analyse', 'reflexion', 'lecture', 'redaction', 'pages', 'tri'];
// Phase de départ : la demande est partie, le serveur la prépare (outils, contexte, prompt)
export const PHASE_DEPART = { code: 'analyse' };

/** Lecture tolérante de `phase` : {code, n?, N?} ; null si le code est inconnu. */
export function normalizePhase(data) {
  const code = data && typeof data === 'object' ? data.phase : null;
  if (!PHASES.includes(code)) return null;
  const { n, N } = data;
  if (code === 'pages' && Number.isInteger(n) && Number.isInteger(N) && n >= 1 && n <= N && N <= PROGRESS_N_MAX) {
    return { code, n, N };
  }
  return { code };
}

const PHASE_LABELS = {
  analyse: 'Analyse de votre demande…',
  reflexion: 'Réflexion en cours…',
  lecture: 'Lecture des résultats…',
  // 1er texte d'un appel : la réponse, ou une phrase d'annonce avant une étape (« Je regarde
  // les pleins… ») ; libellé juste pour les deux
  redaction: 'Rédaction…',
  tri: 'Tri des résultats…',
};

/** Libellé de l'en-tête pendant le flux ; null sans phase (libellé par défaut du panneau). */
export function phaseLabel(phase) {
  if (!phase) return null;
  if (phase.code === 'pages') return phase.N ? `Pages suivantes (${phase.n}/${phase.N})…` : 'Pages suivantes…';
  return PHASE_LABELS[phase.code] ?? null;
}

export function createAssistantMessage({ id, selectedModel = null, now = Date.now() }) {
  return {
    id,
    serverMessageId: null,
    role: 'assistant',
    content: '',
    turns: {},
    movedTurns: [],
    reasoning: { items: [], startedAt: now, endedAt: null, status: 'idle', answerStart: null },
    isStreaming: true,
    outcome: null,
    model: null,
    // Modèle demandé ; remplacé par celui confirmé dans l'événement `start`
    modelId: selectedModel?.id ?? null,
    modelLabel: selectedModel?.label ?? null,
    usage: null,
    stopReason: null,
    dureeMs: null,
    error: null,
    feedback: { rating: null, reason: null, comment: '', status: 'idle' },
    artifacts: [],
    effort: null,
    compaction: null,
    continuations: 0,
    fichiers: [],
    // En-tête « Analyse de votre demande… » dès le clic (lot 1), jamais enregistré
    phase: PHASE_DEPART,
  };
}

// Visuel interrompu par la fin de la réponse (Stop, erreur, coupure) avant sa spécification
export const ARTIFACT_INTERRUPTED = "Le visuel n'a pas pu être terminé.";

/**
 * Ajoute ou met à jour un visuel (clé : artifact_id). Un `artifact` reçu sans
 * `artifact_pending` préalable est ajouté à la suite, dans l'ordre d'arrivée.
 */
function upsertArtifact(message, id, patch) {
  if (id == null || id === '') return message;
  const key = String(id);
  const list = message.artifacts ?? [];
  const index = list.findIndex((a) => a.id === key);
  if (index === -1) {
    const created = { id: key, status: 'pending', kind: null, visual_type: null, title: null, spec: null, message: null, ...patch };
    return { ...message, artifacts: [...list, created] };
  }
  const current = list[index];
  // Un visuel déjà prêt n'est pas ramené à l'état « en préparation » par un doublon
  if (patch.status === 'pending' && current.status !== 'pending') return message;
  const artifacts = [...list];
  artifacts[index] = { ...current, ...patch };
  return { ...message, artifacts };
}

// Réponse finale : concaténation, dans l'ordre, des tours restés dans la réponse
function computeContent(turns, movedTurns) {
  return Object.keys(turns)
    .map(Number)
    .sort((a, b) => a - b)
    .filter((turn) => !movedTurns.includes(turn))
    .map((turn) => turns[turn])
    .filter((text) => text && text.trim())
    .join('\n\n');
}

// Un élément du raisonnement est-il affiché ?
export function isVisibleItem(item) {
  if (item.type === 'narration') return item.visible && !!item.text;
  if (item.type === 'thinking') return !!item.text;
  return true;
}

export function hasVisibleReasoning(message) {
  return !!message.reasoning?.items.some(isVisibleItem);
}

// Nouvelle activité du raisonnement (réflexion, étape, phrase déplacée) : il démarre,
// ou reprend s'il avait été clos par le début d'une réponse
function withReasoning(message, patch, now) {
  const reasoning = { ...message.reasoning, ...patch, answerStart: null };
  if (reasoning.status === 'idle' || (reasoning.status === 'done' && message.isStreaming)) {
    reasoning.status = 'running';
    reasoning.endedAt = null;
  }
  if (!reasoning.startedAt) reasoning.startedAt = now;
  return { ...message, reasoning };
}

const hasRunningStep = (items) => items.some((item) => item.type === 'step' && item.status === 'running');

const indexOfStep = (items, stepId) =>
  stepId == null || stepId === '' ? -1 : items.findIndex((item) => item.type === 'step' && item.step_id === stepId);

function newStep(data, now) {
  return {
    key: nextKey('step'),
    type: 'step',
    step_id: data.step_id,
    tool: data.tool,
    label: data.label || data.tool,
    params: data.params ?? null,
    status: 'running',
    startedAt: now,
    duree_ms: null,
    resume: null,
    error: null,
  };
}

const samePhase = (a, b) => !!a && !!b && a.code === b.code && a.n === b.n && a.N === b.N;

// Le texte du tour suit la fin du raisonnement : celui-ci est clos (replié, durée
// arrêtée) dès que le tour est manifestement la réponse finale
function closeReasoningOnAnswer(reasoning, turn, turnText, now) {
  if (reasoning.status !== 'running' || hasRunningStep(reasoning.items)) return reasoning;
  const answerStart = reasoning.answerStart?.turn === turn ? reasoning.answerStart : { turn, at: now };
  if (turnText.length < ANSWER_MIN_CHARS && !turnText.includes('\n')) {
    return reasoning.answerStart === answerStart ? reasoning : { ...reasoning, answerStart };
  }
  return { ...reasoning, answerStart, status: 'done', endedAt: answerStart.at };
}

export function applyStreamEvent(message, type, data, now = Date.now()) {
  switch (type) {
    case 'start':
      return {
        ...message,
        serverMessageId: data.message_id ?? message.serverMessageId,
        model: data.model ?? message.model,
        modelId: data.modelId ?? message.modelId,
        modelLabel: data.label ?? message.modelLabel,
        effort: normalizeEffortInfo(data.effort) ?? message.effort ?? null,
      };

    case 'context_compacted': {
      const compaction = normalizeCompaction(data);
      return compaction ? { ...message, compaction } : message;
    }

    case 'continuation': {
      const n = count(data.n);
      const previous = message.continuations ?? 0;
      return { ...message, continuations: n != null && n > previous ? n : previous + 1 };
    }

    case 'thinking_delta': {
      if (!data.text) return message;
      const items = [...message.reasoning.items];
      const last = items[items.length - 1];
      // Appel suivant du modèle (repère posé par `phase` reflexion) : sa réflexion a son propre bloc
      if (last?.type === 'thinking' && !message.reasoning.nouvelAppel) {
        items[items.length - 1] = { ...last, text: last.text + data.text };
      } else {
        items.push({ key: nextKey('thinking'), type: 'thinking', text: data.text });
      }
      if (!message.reasoning.nouvelAppel) return withReasoning(message, { items }, now);
      const { nouvelAppel, ...reasoning } = message.reasoning;
      return withReasoning({ ...message, reasoning }, { items }, now);
    }

    case 'text_delta': {
      const turn = Number(data.turn ?? 0);
      const text = data.text ?? '';
      if (!text) return message;
      const turns = { ...message.turns, [turn]: (message.turns[turn] ?? '') + text };

      // Premier texte du tour : on réserve sa place dans la chronologie du raisonnement,
      // au cas où le tour se terminerait par un appel d'outil
      let reasoning = message.reasoning;
      if (!(turn in message.turns)) {
        reasoning = {
          ...reasoning,
          items: [...reasoning.items, { key: nextKey('narration'), type: 'narration', turn, text: '', visible: false }],
        };
      }
      reasoning = closeReasoningOnAnswer(reasoning, turn, turns[turn], now);

      return { ...message, turns, reasoning, content: computeContent(turns, message.movedTurns) };
    }

    case 'turn_to_reasoning': {
      const turn = Number(data.turn ?? 0);
      if (message.movedTurns.includes(turn)) return message;
      const movedTurns = [...message.movedTurns, turn];
      const text = (message.turns[turn] ?? '').trim();
      const items = message.reasoning.items.map((item) =>
        item.type === 'narration' && item.turn === turn ? { ...item, text, visible: true } : item
      );
      const next = { ...message, movedTurns, content: computeContent(message.turns, movedTurns) };
      return text ? withReasoning(next, { items }, now) : { ...next, reasoning: { ...message.reasoning, items } };
    }

    case 'step_start': {
      // Étape déjà présente (annoncée par step_pending, ou step_start répété) : précisée sur
      // place, même clé et même début, jamais en double
      const index = indexOfStep(message.reasoning.items, data.step_id);
      if (index !== -1) {
        const items = [...message.reasoning.items];
        const { pending, ouvreRaisonnement, ...current } = items[index];
        items[index] = { ...current, tool: data.tool ?? current.tool, label: data.label || data.tool || current.label, params: data.params ?? current.params ?? null };
        // Étape annoncée qui commence : le raisonnement reprend comme à un step_start d'avant le
        // lot 1 (début de réponse oublié) ; step_start répété : rien d'autre ne change
        return pending
          ? withReasoning({ ...message, phase: null }, { items }, now)
          : { ...message, phase: null, reasoning: { ...message.reasoning, items } };
      }
      return withReasoning({ ...message, phase: null }, { items: [...message.reasoning.items, newStep(data, now)] }, now);
    }

    // Étape annoncée dès le début du bloc tool_use (lot 1), avant ses arguments ; précisée par
    // step_start, ou retirée (retire) si le bloc n'est jamais exécuté. Message inchangé (même
    // objet) pour un doublon ou un retrait sans objet.
    // L'annonce ne touche pas à l'état du raisonnement (statut, fin, début de réponse), qu'un bloc
    // jamais commencé laissait intact avant le lot 1 : elle l'ouvre seulement s'il est inactif
    // (signe de vie), et un retrait sans autre activité depuis le rend inactif. Raisonnement déjà
    // clos par la réponse : aucune annonce, step_start le rouvre comme avant
    case 'step_pending': {
      if (data.step_id == null || data.step_id === '') return message;
      const { items } = message.reasoning;
      const index = indexOfStep(items, data.step_id);
      if (data.retire) {
        if (index === -1 || !items[index].pending) return message;
        const reasoning = { ...message.reasoning, items: items.filter((_, i) => i !== index) };
        if (items[index].ouvreRaisonnement && reasoning.status === 'running' && !reasoning.items.some(isVisibleItem)) {
          reasoning.status = 'idle';
          reasoning.endedAt = null;
        }
        return { ...message, reasoning };
      }
      if (index !== -1 || message.reasoning.status === 'done') return message;
      const step = { ...newStep(data, now), pending: true };
      if (message.reasoning.status !== 'idle') {
        return { ...message, phase: null, reasoning: { ...message.reasoning, items: [...items, step] } };
      }
      return withReasoning({ ...message, phase: null }, { items: [...items, { ...step, ouvreRaisonnement: true }] }, now);
    }

    // Phase vraie du serveur (lot 1) : en-tête seulement, ni étape ni enregistrement
    // « reflexion » marque aussi le début d'un nouvel appel du modèle (émise une fois par appel,
    // avant son 1er jeton de réflexion) : si la réflexion précédente est encore le dernier élément
    // (appel coupé ou rejoué sans étape, réponse vide relancée), la suivante ne s'y colle pas
    case 'phase': {
      const phase = normalizePhase(data);
      if (!phase || !message.isStreaming) return message;
      const items = message.reasoning.items;
      const next = phase.code === 'reflexion' && items[items.length - 1]?.type === 'thinking' && !message.reasoning.nouvelAppel
        ? { ...message, reasoning: { ...message.reasoning, nouvelAppel: true } }
        : message;
      return samePhase(next.phase, phase) ? next : { ...next, phase };
    }

    // Avancement d'une étape en cours (v1.8) : repère du compteur, puis « étape n/N ». Aucun
    // autre changement (le raisonnement n'est pas rouvert) ; message inchangé (même objet) si
    // l'étape est inconnue, terminée, ou si rien ne change (n qui recule, doublon)
    case 'step_progress': {
      const info = normalizeStepProgress(data);
      if (!info) return message;
      let changed = false;
      const items = message.reasoning.items.map((item) => {
        if (item.type !== 'step' || item.step_id !== info.step_id || item.status !== 'running') return item;
        let next = item;
        if (item.execStartedAt == null && info.ecoule_ms != null) next = { ...next, execStartedAt: now - info.ecoule_ms };
        const current = item.progress ?? null;
        if (info.progress && !(current && info.progress.n < current.n) && !sameProgress(current, info.progress)) {
          next = { ...next, progress: info.progress };
        }
        if (next !== item) changed = true;
        return next;
      });
      return changed ? { ...message, reasoning: { ...message.reasoning, items } } : message;
    }

    case 'step_done': {
      const items = message.reasoning.items.map((item) => {
        if (item.type !== 'step' || item.step_id !== data.step_id) return item;
        const { pending, ouvreRaisonnement, ...step } = item;
        return {
          ...step,
          status: data.status === 'error' ? 'error' : 'ok',
          duree_ms: data.duree_ms ?? now - item.startedAt,
          resume: data.resume ?? null,
          error: data.error ?? null,
          progress: null,
          endedAt: item.endedAt ?? now,
        };
      });
      // « Pages suivantes (n/N)… » appartient à l'étape : close avec elle
      const phase = message.phase?.code === 'pages' ? null : message.phase;
      return { ...message, phase, reasoning: { ...message.reasoning, items } };
    }

    case 'usage':
      return { ...message, usage: data };

    // Liens vérifiés de fichiers (v1.13) : ajoutés à ceux du message, sans doublon
    case 'file_links': {
      const nouveaux = normalizeFichiers(data?.fichiers, data?.expire_le);
      return nouveaux.length ? { ...message, fichiers: fusionnerFichiers(message.fichiers, nouveaux) } : message;
    }

    case 'done':
      return finalizeMessage({
        ...message,
        serverMessageId: message.serverMessageId ?? data.message_id ?? null,
        stopReason: data.stop_reason ?? null,
        dureeMs: data.duree_ms ?? null,
        usage: data.usage ?? message.usage,
        effort: normalizeEffortInfo(data.effort) ?? message.effort ?? null,
        // Nombre total de continuations, aussi porté par `done` (backend sprint 6)
        continuations: Math.max(message.continuations ?? 0, count(data.continuations) ?? 0),
      }, 'done', now);

    case 'error':
      return finalizeMessage({
        ...message,
        error: { code: data.code ?? 'ERROR', message: data.message ?? 'Erreur inconnue' },
      }, 'error', now);

    // `end` sans `done` ni `error` : la réponse n'est pas arrivée au bout
    case 'end':
      return message.isStreaming
        ? finalizeMessage({ ...message, error: message.error ?? CONNECTION_LOST }, 'error', now)
        : message;

    // Visuels (contrat C0, §1.1) : skeleton typé, puis spécification validée ou refus
    // Peut être réémis pour préciser le type et le titre (contrat v1.1, §1.2) : seuls les
    // champs fournis sont mis à jour
    case 'artifact_pending': {
      const patch = { status: 'pending' };
      for (const key of ['kind', 'visual_type', 'title', 'format']) if (data[key] != null) patch[key] = data[key];
      return upsertArtifact(message, data.artifact_id, patch);
    }

    case 'artifact': {
      if (!data.spec || typeof data.spec !== 'object') {
        return upsertArtifact(message, data.artifact_id, { status: 'error', message: 'Visuel reçu incomplet.' });
      }
      return upsertArtifact(message, data.artifact_id, {
        status: 'ready',
        spec: data.spec,
        kind: data.spec.kind ?? null,
        visual_type: data.spec.visual_type ?? null,
        title: data.spec.title ?? null,
        message: null,
        ...(data.spec.format != null ? { format: data.spec.format } : {}),
      });
    }

    case 'artifact_error':
      return upsertArtifact(message, data.artifact_id, {
        status: 'error',
        spec: null,
        message: data.message ?? "Ce visuel n'a pas pu être construit.",
      });

    default:
      return message;
  }
}

/**
 * Clôt le message : fin normale, erreur, annulation (Stop) ou connexion coupée.
 * @param {'done'|'error'|'stopped'} outcome
 */
export function finalizeMessage(message, outcome, now = Date.now()) {
  const items = message.reasoning.items
    // Tours restés dans la réponse : leur place réservée n'a plus d'utilité
    .filter((item) => item.type !== 'narration' || item.visible)
    // Étapes seulement annoncées (step_pending), jamais commencées : rien à garder, comme
    // avant le lot 1 où elles n'apparaissaient qu'au step_start
    .filter((item) => !(item.type === 'step' && item.pending))
    .map((item) => (item.type === 'step' && item.status === 'running'
      ? { ...item, status: 'cancelled', duree_ms: now - item.startedAt, progress: null }
      : item));

  const status = outcome === 'done' && message.error ? 'error' : outcome;

  // Visuels encore « en préparation » : la réponse est close, ils ne viendront plus
  const artifacts = (message.artifacts ?? []).map((a) => (a.status === 'pending'
    ? { ...a, status: 'error', message: ARTIFACT_INTERRUPTED }
    : a));

  // Raisonnement : inchangé s'il n'a jamais commencé, ou s'il était déjà clos quand
  // la réponse s'est arrêtée (Stop ou erreur pendant l'écriture de la réponse)
  let reasoningStatus = status;
  // (aucun élément gardé : jamais commencé, y compris quand seule une étape annoncée l'avait ouvert)
  if (!items.length && (message.reasoning.status === 'idle' || message.reasoning.items.some((item) => item.pending))) reasoningStatus = 'idle';
  else if (message.reasoning.status === 'done') reasoningStatus = 'done';

  return {
    ...message,
    isStreaming: false,
    outcome: status,
    phase: null,
    artifacts,
    reasoning: {
      ...message.reasoning,
      items,
      status: reasoningStatus,
      endedAt: message.reasoning.endedAt ?? now,
    },
  };
}

/**
 * Message « rapport composé » (DOC-17) : document en préparation (skeleton), remplacé
 * par la spécification renvoyée par POST /api/reports/compose, ou par une note d'erreur.
 */
export function createReportMessage({ id, title, format, count, now = Date.now() }) {
  const n = Number(count) || 0;
  return {
    ...finalizeMessage(createAssistantMessage({ id, now }), 'done', now),
    origin: 'report',
    content: `Rapport « ${title} » composé à partir de ${n} élément${n > 1 ? 's' : ''} (sans appel à l'IA).`,
    reasoning: { items: [], startedAt: null, endedAt: null, status: 'idle', answerStart: null },
    artifacts: [{ id: `${id}:document`, status: 'pending', kind: 'document', visual_type: null, title, spec: null, message: null, format }],
  };
}

/** Historique envoyé au serveur : rôle + réponse finale uniquement (sans les rapports composés). */
export function toHistory(messages) {
  return messages
    .filter((m) => m.origin !== 'report')
    .filter((m) => (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string' && m.content.trim())
    .map((m) => ({ role: m.role, content: m.content }));
}
